/* global G */
import { pipe } from 'lib/util'
import { withDescriptor, withObjectFreeze } from 'trait/with'
import { hasNamespace } from 'trait/has'
import { usesGetterSetter, usesNamespace } from 'trait/uses'
import { withGetterSetterFn } from 'trait/with/GetterSetter'
import api from 'platform/adapter/pin/api'

const descriptor = 'adapter:Pin'

/**
 * @typedef {Object} PinError
 * @property {string} type    type of the error
 * @property {string} title   title for the error
 * @property {string} message the message
 */

/**
 * Type definition for a PIN.
 *
 * @export
 * @typedef {Object} PIN
 * @property {string|Hex} KEY     information about the machine and software
 * @property {Object} INFO        additional information
 * @property {string} INFO.Magic  the magic bit
 * @property {string} INFO.Time   a timestamp when the pin was created
 */

/**
 * Type definition for the contents of a decrypted PIN.
 *
 * @typedef {Object} PinContent
 * @property {PIN.KEY} key            information about the machine and software
 * @property {PIN.INFO.Magic} magic   the magic bit to get the readable PIN from
 * @property {string} serial          serial number of the machine
 * @property {string} software        software version of the machine
 * @property {PIN.INFO.Time} created  when the QR code was created
 * @property {Object} request         raw data of the decrypted code
 */

/**
 * Type definition for a fully parsed PIN.
 *
 * @typedef {Object} ParsedPin
 * @property {string} pin                   the human-readable PIN
 * @property {PinContent.software} software software version of the machine
 * @property {PinContent.serial} serial     serial number of the machine
 * @property {PinContent.request} request   raw data from the decrypted code
 * @property {PinError} [error=null]        optional error
 */

const defaults = {
  init: G.INIT,
  create: G.CREATE,
}

/**
 * Attaches all api methods whose name can be found within {@link defaults} to the event handler by
 * assigning them an event type in the form:
 *
 * @example
 * const groupName = 'default'
 * eventBus.type(G.NOTIFICATION, G.CREATE, groupName) === 'gaia:notification:create:default'
 *
 * @param {Gaia.Web.Application} obj  the Platform Web Application
 * @returns {(function(*): void)|*}
 */
const withEventHandlers = obj => (adapter) => {
  /** @type {Gaia.Adapter.EventBus} */
  const eventBus = obj[G.EVENTS]

  Object.keys(defaults).reduce(
    (acc, key) => eventBus.add(eventBus.type(G.PIN, acc[key]), adapter[G.API][key]) || acc,
    defaults,
  )

  return adapter
}
/**
 * Adapter Namespace
 *
 * Contains entries of native platform adapters
 *
 * @namespace Gaia.Adapter
 * @memberOf Gaia
 */

/**
 * Native Platform Web Adapter Pin
 *
 * Provides facilities to create a PIN from a QR-code
 *
 * @memberOf Gaia.Adapter
 * @namespace Pin
 * @typedef Pin
 *
 * @property {String} _name                 module descriptor. see {@link withDescriptor}
 * @property {Object} G.API                 adapter api
 * @property {Adapter.Attachment.API} API   adapter api revised
 *
 * @param {Gaia.Web.app} obj                web application
 * @returns {Gaia.Adapter.Cart}              composition - Native Platform Web Adapter
 */
const adapterFn = obj => pipe(
  withDescriptor(descriptor),
  withGetterSetterFn(G.STATE),
  usesGetterSetter(G.STATE, { privateKeys: [] }),
  hasNamespace(G.API),
  usesNamespace(G.API, api(obj)),
  withEventHandlers(obj),
  withObjectFreeze,
)({})

export default adapterFn
